/**
 * useMcpAppBridge — React hook for managing the host-side AppBridge lifecycle.
 *
 * This hook wraps the AppBridge from @modelcontextprotocol/ext-apps to provide
 * a React-friendly API for rendering MCP App Views in sandboxed iframes.
 *
 * Lifecycle:
 * 1. Create AppBridge (null MCP client — we handle tool calls manually)
 * 2. Connect via PostMessageTransport to the iframe
 * 3. Wait for View initialization
 * 4. Send tool input and result
 * 5. Teardown on unmount
 */
import {
    AppBridge,
    PostMessageTransport
} from '@modelcontextprotocol/ext-apps/app-bridge';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ToolCallResult, ToolResultContent } from './types';

export interface UseMcpAppBridgeOptions {
    /** Callback when the View sends a message to the conversation */
    onMessage?: (content: ToolResultContent[]) => void;
    /** Callback when the View updates model context */
    onModelContextUpdate?: (
        content: ToolResultContent[],
        structured?: Record<string, unknown>
    ) => void;
    /** Callback when the View wants to call a server tool */
    onToolCall?: (
        name: string,
        args: Record<string, unknown>
    ) => Promise<ToolCallResult>;
    /** Callback when the View wants to open an external link */
    onOpenLink?: (url: string) => void;
    /** Callback when the View resizes */
    onSizeChange?: (width?: number, height?: number) => void;
    /** Current theme */
    theme?: 'light' | 'dark';
    /** Display mode */
    displayMode?: 'inline' | 'fullscreen' | 'pip';
}

export interface UseMcpAppBridgeReturn {
    /** Ref to attach to the iframe element */
    iframeRef: React.RefObject<HTMLIFrameElement | null>;
    /** Whether the bridge is connected and the View is initialized */
    isInitialized: boolean;
    /** Whether we're still in the connection/init phase */
    isConnecting: boolean;
    /** Error from the bridge */
    error: string | null;
    /** Send tool input to the View */
    sendToolInput: (args: Record<string, unknown>) => void;
    /** Send tool result to the View */
    sendToolResult: (result: ToolCallResult) => void;
    /** Setup the bridge with the given iframe (call after iframe loads) */
    setupBridge: () => void;
    /** Teardown the bridge */
    teardown: () => Promise<void>;
}

export function useMcpAppBridge(
    options: UseMcpAppBridgeOptions = {}
): UseMcpAppBridgeReturn {
    const {
        onMessage,
        onToolCall,
        onOpenLink,
        onSizeChange,
        onModelContextUpdate,
        theme = 'light',
        displayMode = 'inline'
    } = options;

    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const bridgeRef = useRef<AppBridge | null>(null);
    // Queued payloads to send as soon as the bridge is ready
    const pendingToolResultRef = useRef<ToolCallResult | null>(null);
    const pendingToolInputRef = useRef<Record<string, unknown> | null>(null);

    const [isInitialized, setIsInitialized] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Keep callbacks in refs so the bridge handlers always see latest
    const onMessageRef = useRef(onMessage);
    const onToolCallRef = useRef(onToolCall);
    const onOpenLinkRef = useRef(onOpenLink);
    const onSizeChangeRef = useRef(onSizeChange);
    const onModelContextUpdateRef = useRef(onModelContextUpdate);

    useEffect(() => {
        onMessageRef.current = onMessage;
        onToolCallRef.current = onToolCall;
        onOpenLinkRef.current = onOpenLink;
        onSizeChangeRef.current = onSizeChange;
        onModelContextUpdateRef.current = onModelContextUpdate;
    });

    const setupBridge = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) return;

        // Clean up previous bridge
        if (bridgeRef.current) {
            bridgeRef.current.teardownResource({}).catch(() => {});
        }

        setIsConnecting(true);
        setError(null);

        try {
            // Create bridge WITHOUT an MCP client — we handle tool calls manually
            const bridge = new AppBridge(
                null, // No MCP client; we register manual handlers
                {
                    name: 'DataMinds Chat',
                    version: '1.0.0'
                },
                {
                    // Declare host capabilities
                    openLinks: {},
                    serverTools: {},
                    logging: {},
                    message: {}
                }
            );

            // --- Register handlers ---

            // When the View finishes initialization
            bridge.oninitialized = () => {
                // Flush queued data in a microtask — connect() must fully resolve
                // before the bridge transport accepts outgoing messages.
                Promise.resolve().then(() => {
                    if (pendingToolInputRef.current) {
                        try {
                            bridge.sendToolInput({
                                arguments: pendingToolInputRef.current
                            });
                        } catch {}
                        pendingToolInputRef.current = null;
                    }
                    if (pendingToolResultRef.current) {
                        try {
                            bridge.sendToolResult(
                                pendingToolResultRef.current as any
                            );
                        } catch {}
                        pendingToolResultRef.current = null;
                    }
                    setIsInitialized(true);
                    setIsConnecting(false);
                });
            };

            // When the View wants to call a server tool
            (bridge as any).oncalltool = async (params: any) => {
                if (!onToolCallRef.current) {
                    return {
                        content: [
                            {
                                type: 'text' as const,
                                text: 'No tool call handler registered'
                            }
                        ],
                        isError: true
                    };
                }
                try {
                    return await onToolCallRef.current(
                        params.name,
                        params.arguments ?? {}
                    );
                } catch (err) {
                    return {
                        content: [
                            {
                                type: 'text' as const,
                                text: `Error: ${
                                    err instanceof Error
                                        ? err.message
                                        : String(err)
                                }`
                            }
                        ],
                        isError: true
                    };
                }
            };

            bridge.onmessage = async ({ content }: any) => {
                onMessageRef.current?.(content as ToolResultContent[]);
                return {};
            };
            bridge.onopenlink = async ({ url }: any) => {
                (
                    onOpenLinkRef.current ??
                    ((u: string) =>
                        window.open(u, '_blank', 'noopener,noreferrer'))
                )(url);
                return {};
            };
            bridge.onsizechange = ({ width, height }: any) => {
                onSizeChangeRef.current?.(width, height);
            };
            bridge.onupdatemodelcontext = async ({
                content,
                structuredContent
            }: any) => {
                onModelContextUpdateRef.current?.(
                    (content ?? []) as ToolResultContent[],
                    structuredContent
                );
                return {};
            };

            bridgeRef.current = bridge;
            const transport = new PostMessageTransport(
                iframe.contentWindow,
                iframe.contentWindow
            );

            bridge
                .connect(transport)
                .then(() => {
                    try {
                        bridge.setHostContext({
                            theme,
                            displayMode,
                            availableDisplayModes: ['inline', 'fullscreen'],
                            platform: 'web'
                        });
                    } catch {}
                })
                .catch((err: any) => {
                    setError(
                        `Bridge connection failed: ${
                            err instanceof Error ? err.message : String(err)
                        }`
                    );
                    setIsConnecting(false);
                });
        } catch (err) {
            setError(
                `Failed to create AppBridge: ${
                    err instanceof Error ? err.message : String(err)
                }`
            );
            setIsConnecting(false);
        }
    }, [theme, displayMode]);

    const sendToolInput = useCallback(
        (args: Record<string, unknown>) => {
            if (!bridgeRef.current || !isInitialized) {
                pendingToolInputRef.current = args;
                return;
            }
            try {
                bridgeRef.current.sendToolInput({ arguments: args });
            } catch {}
        },
        [isInitialized]
    );

    const sendToolResult = useCallback(
        (result: ToolCallResult) => {
            if (!bridgeRef.current || !isInitialized) {
                pendingToolResultRef.current = result;
                return;
            }
            try {
                bridgeRef.current.sendToolResult(result as any);
            } catch {}
        },
        [isInitialized]
    );

    const teardown = useCallback(async () => {
        if (bridgeRef.current) {
            bridgeRef.current.teardownResource({}).catch(() => {});
            bridgeRef.current = null;
        }
        pendingToolResultRef.current = null;
        pendingToolInputRef.current = null;
        setIsInitialized(false);
        setIsConnecting(false);
    }, []);

    useEffect(() => {
        if (bridgeRef.current && isInitialized) {
            try {
                bridgeRef.current.setHostContext({ theme });
            } catch {}
        }
    }, [theme, isInitialized]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            teardown();
        };
    }, [teardown]);

    return {
        iframeRef,
        isInitialized,
        isConnecting,
        error,
        sendToolInput,
        sendToolResult,
        setupBridge,
        teardown
    };
}
